import React, { useState } from 'react';
import { IModalComponent } from '~/components/Modal';
import { TableSort, TableSortDirection } from '~/components/Table';
import { Button } from '~/components/Button';
import { Title } from '~/components/Title';
import { Intent } from '~/lib/intent';
import { IFinancePayment } from '~/modules/Payments/api';
import { sortByString, SortFunction } from '~/lib/sort';
import { endpoint } from '~/api';

interface IInvoicePaymentsModalProps {
  sellerId: number;
  payments: IFinancePayment[];
}

const sortByProcessedAt: SortFunction<IFinancePayment> = (records, fieldName, direction) => {
  return [...records].sort((a, b) => {
    const aRaw = a[fieldName as keyof IFinancePayment] as string | null | undefined;
    const bRaw = b[fieldName as keyof IFinancePayment] as string | null | undefined;

    const aTime = aRaw ? new Date(aRaw).getTime() : 0;
    const bTime = bRaw ? new Date(bRaw).getTime() : 0;

    if (aTime === bTime) return 0;
    return (aTime > bTime ? 1 : -1) * direction;
  });
};

const sortByAmount: SortFunction<IFinancePayment> = (records, fieldName, direction) => {
  return [...records].sort((a, b) => {
    const aVal = Number(a[fieldName as keyof IFinancePayment] ?? 0);
    const bVal = Number(b[fieldName as keyof IFinancePayment] ?? 0);

    if (aVal === bVal) return 0;
    return (aVal > bVal ? 1 : -1) * direction;
  });
};

export const InvoicePaymentsModal: IModalComponent<IInvoicePaymentsModalProps> = (props) => {
  const { sellerId, payments, close } = props;
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const download = async (p: IFinancePayment) => {
    setDownloadingId(p.id);
    try {
      const blob = await endpoint
        .post(`/invoice/seller/${sellerId}`, p, { responseType: 'blob' })
        .then((r) => r.data as Blob);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${p.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div style={{ minWidth: 720 }}>
      <Title>Invoices</Title>
      {payments.length === 0 ? (
        <p>No invoiceable payments.</p>
      ) : (
        <TableSort
          records={payments}
          defaultSort={{
            sort: sortByProcessedAt,
            field: 'processedAt',
            direction: TableSortDirection.DESC,
          }}
          header
          columns={[
            {
              header: 'Memo / Period',
              field: 'memo',
              cell: (p) => p.memo || '—',
            },
            {
              header: 'Amount',
              field: 'sourceAmount',
              cell: (p) => p.sourceAmount,
              sort: sortByAmount,
            },
            {
              header: 'Status',
              field: 'status',
              cell: (p) => p.status,
            },
            {
              header: 'Processed at',
              field: 'processedAt',
              cell: (p) =>
                p.processedAt ? new Date(p.processedAt).toLocaleDateString() : '—',
              sort: sortByProcessedAt,
            },
            {
              small: true,
              cell: (p) => (
                <Button
                  small
                  intent={Intent.SUCCESS}
                  onClick={() => download(p)}
                  disabled={downloadingId === p.id}
                >
                  {downloadingId === p.id ? 'Downloading…' : 'Download'}
                </Button>
              ),
            },
          ]}
        />
      )}

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Button onClick={close}>Close</Button>
      </div>
    </div>
  );
};
